import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorFallbackProps {
  error?: Error | null;
  isChunkError?: boolean;
  onReload?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, isChunkError = false, onReload }) => {
  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          maxWidth: '500px',
          textAlign: 'center',
          backgroundColor: '#fff',
          padding: '3rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            marginBottom: '1rem',
            fontWeight: 600,
            color: '#333',
          }}
        >
          {isChunkError ? 'Update Available' : 'Something went wrong'}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            marginBottom: '2rem',
            color: '#666',
            lineHeight: 1.6,
          }}
        >
          {isChunkError
            ? 'A new version of the application is available. Please reload the page to get the latest updates.'
            : 'An unexpected error occurred. Please try reloading the page.'}
        </Typography>

        {error && !isChunkError && (
          <Box
            sx={{
              marginBottom: '2rem',
              padding: '1rem',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              textAlign: 'left',
              maxHeight: '150px',
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                color: '#d32f2f',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleReload}
          sx={{
            textTransform: 'none',
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          Reload Page
        </Button>
      </Box>
    </Box>
  );
};

export default ErrorFallback;
